import { Label, LabelLanguages } from '@telespot/domain';

// Type-only: `PipelineStepCategory` imports this service for `isProperlyDefined`,
// so a value import back into `../../types` would close a runtime cycle.
import type { LabelReference, Language, LocalizedName } from '../../types';

/**
 * Domain-side projector that assembles per-language `Label` records (one row
 * per uuid+language pair) into `LocalizedName` objects indexed by label uuid.
 *
 * Its rows come from whichever adapter implements the label-name port; the
 * grouping rule lives here because promoting a fallback language into English
 * is a rule about names rather than about how they were read.
 */
export class LabelNameProjector {
  /**
   * Groups flat label records into a `LocalizedName` per uuid. Every returned
   * name has `en` filled: when no English record exists, a fallback language
   * value is promoted so consumers always have a displayable default.
   */
  public static groupByUuid(labels: Label[]): Record<string, LocalizedName> {
    const result: Record<string, LocalizedName> = {};
    for (const label of labels) {
      const uuid = label.uuid;
      const lang = label.lang as Language;
      const existing = result[uuid];
      if (existing) {
        existing[lang] = label.value;
      } else {
        const localized: LocalizedName = { en: '' };
        localized[lang] = label.value;
        result[uuid] = localized;
      }
    }
    for (const uuid of Object.keys(result)) {
      if (!result[uuid].en) {
        result[uuid].en = this.pickFallbackName(result[uuid]);
      }
    }
    return result;
  }

  private static pickFallbackName(name: LocalizedName): string {
    return name.es ?? name.fr ?? name.pt ?? '';
  }

  /**
   * Whether a label reference carries a real name, as opposed to either shape
   * that means "we could not name this": the uuid-as-name placeholder the draft
   * projector hands out, and a blank English name — which is what `groupByUuid`
   * emits for a label whose only stored value is outside the fallback chain.
   *
   * Comparing the name to the uuid is safe from coincidence: category and option
   * uuids are always randomly generated, never user-authored, while the name is
   * separately typed free text or taken from a stored label. No path feeds both
   * from one field.
   *
   * Lives here, on the projector that produces the names, so the banner, the row
   * markers and the lookup retry cannot disagree about what "properly defined"
   * means.
   */
  public static isProperlyDefined(reference: LabelReference): boolean {
    // `en` is required by `LocalizedName`, but these names arrive from persisted
    // params and hand-edited JSON, so a missing one is judged rather than thrown on.
    const englishName = (reference.name.en ?? '').trim();
    return englishName.length > 0 && englishName !== reference.uuid;
  }

  /**
   * Resolves the single display name for a label given the app's current
   * language: current language → English → first non-empty value → `''`.
   *
   * Deliberately a DIFFERENT rule than `pickFallbackName` (fixed `es → fr → pt`
   * order, used to guarantee `groupByUuid`'s `en` default): this one is
   * viewer-relative — what the user should read in a banner/snackbar — while
   * `pickFallbackName` is a storage-side default for records missing English.
   */
  public static resolveDisplayName(languages: LabelLanguages, currentLang: string): string {
    const dict = languages as Record<string, string | undefined>;
    return (
      dict[currentLang] ||
      languages.en ||
      Object.values(languages).find((value): value is string => typeof value === 'string' && value.length > 0) ||
      ''
    );
  }
}
